<template>
  <!--
    StepConfirmation.vue - Step 4: Bekræftelse (NY STRUKTUR)
    ========================================================
    Viser et overblik over alle indtastede data før endelig indsendelse.

    NY WIZARD STRUKTUR:
    - Step 1: Grundlag (alle dokumenter)
    - Step 2: Skyldner
    - Step 3: Ekstra information (kun hvis påkrævet)
    - Step 4: Bekræftelse (denne komponent)

    Brugeren kan:
    - Se alle data samlet på ét sted
    - Klikke på sektioner for at gå tilbage og rette
    - Bekræfte og oprette sagen
  -->
  <div class="step-confirmation">
    <!-- Overskrift -->
    <div class="step-header mb-2">
      <h4>{{ $t('DEBTIA_CREATE_CONFIRM_TITLE') }}</h4>
      <p class="text-muted mb-0">
        {{ $t('DEBTIA_CREATE_CONFIRM_SUBTITLE') }}
      </p>
    </div>

    <!-- Sagstype & Dokumenter (Step 1) -->
    <b-card
      class="confirmation-section"
      @click="goToStep(1)"
    >
      <div class="section-header">
        <h5>
          <feather-icon
            icon="FileTextIcon"
            class="mr-50"
          />
          {{ $t('DEBTIA_DOCUMENTS') }}
        </h5>
        <feather-icon
          icon="EditIcon"
          class="edit-icon"
        />
      </div>
      <div class="section-content">
        <!-- Sagstype -->
        <div class="mb-2">
          <span class="text-muted">{{ $t('DEBTIA_CREATE_CONFIRM_CASE_TYPE') }}:</span>
          <strong class="ml-1">{{ selectedCaseType ? selectedCaseType.nameDa : $t('DEBTIA_CREATE_CONFIRM_INVOICE') }}</strong>
          <b-badge
            v-if="caseData.caseType.aiDetermined"
            variant="light-primary"
            class="ml-50"
          >
            {{ $t('DEBTIA_CREATE_CONFIRM_AI_DETERMINED') }}
          </b-badge>
        </div>

        <!-- Grundlag / Fakturaer -->
        <h6>{{ $t('DEBTIA_BASIS_INVOICES') }} ({{ caseData.documents.grundlag.length }})</h6>
        <b-table-simple
          v-if="caseData.documents.grundlag.length > 0"
          small
          responsive
          class="mb-2"
        >
          <b-thead>
            <b-tr>
              <b-th>{{ $t('DEBTIA_TITLE') }}</b-th>
              <b-th>{{ $t('DEBTIA_CREATE_CONFIRM_TH_INVOICE_DATE') }}</b-th>
              <b-th>{{ $t('DEBTIA_CREATE_CONFIRM_TH_DUE') }}</b-th>
              <b-th class="text-right">
                {{ $t('DEBTIA_AMOUNT') }}
              </b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="doc in caseData.documents.grundlag"
              :key="doc.id"
            >
              <b-td>{{ doc.title || $t('DEBTIA_CREATE_CONFIRM_NO_TITLE') }}</b-td>
              <b-td>{{ formatDate(doc.sentAt) }}</b-td>
              <b-td>{{ formatDate(doc.dueDate) }}</b-td>
              <b-td class="text-right">
                {{ formatAmount(doc.paymentAmount) }} kr
                <div
                  v-if="doc.amountPaid && parseFloat(doc.amountPaid) > 0"
                  class="small text-success"
                >
                  {{ $t('DEBTIA_CREATE_CONFIRM_OF_WHICH_PAID') }} {{ formatAmount(doc.amountPaid) }} kr
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
          <b-tfoot>
            <b-tr>
              <b-th colspan="3">
                {{ $t('DEBTIA_REMAINING') }}
              </b-th>
              <b-th class="text-right">
                {{ formatAmount(totalAmount - totalPaid) }} kr
              </b-th>
            </b-tr>
          </b-tfoot>
        </b-table-simple>

        <!-- Rykkere -->
        <div v-if="caseData.documents.rykkere.length > 0">
          <h6>{{ $t('DEBTIA_REMINDERS') }} ({{ caseData.documents.rykkere.length }})</h6>
          <ul class="mb-2">
            <li
              v-for="reminder in caseData.documents.rykkere"
              :key="reminder.id"
            >
              {{ reminder.file ? reminder.file.name : $t('DEBTIA_CREATE_CONFIRM_NO_FILE') }}
              <span v-if="reminder.reminderFee"> - {{ $t('DEBTIA_CREATE_CONFIRM_FEE') }}: {{ reminder.reminderFee }} kr</span>
            </li>
          </ul>
        </div>

        <!-- Kreditnotaer -->
        <div v-if="caseData.documents.kreditnoter.length > 0">
          <h6>{{ $t('DEBTIA_CREDIT_NOTES') }} ({{ caseData.documents.kreditnoter.length }})</h6>
          <ul class="mb-2">
            <li
              v-for="note in caseData.documents.kreditnoter"
              :key="note.id"
            >
              {{ formatDate(note.sentAt) }} - {{ formatAmount(note.creditNoteAmount) }} kr
            </li>
          </ul>
        </div>

        <!-- Øvrig dokumentation -->
        <div v-if="caseData.documents.oevrig.length > 0">
          <h6>{{ $t('DEBTIA_OTHER_DOCUMENTATION') }} ({{ caseData.documents.oevrig.length }})</h6>
          <ul class="mb-0">
            <li
              v-for="file in caseData.documents.oevrig"
              :key="file.id"
            >
              {{ file.title || (file.file && file.file.name) || $t('DEBTIA_CREATE_CONFIRM_NO_TITLE') }}
            </li>
          </ul>
        </div>
      </div>
    </b-card>

    <!-- Skyldner(e) (Step 2) -->
    <b-card
      class="confirmation-section"
      @click="goToStep(2)"
    >
      <div class="section-header">
        <h5>
          <feather-icon
            icon="UserIcon"
            class="mr-50"
          />
          {{ caseData.debtors.length > 1 ? $t('DEBTIA_CREATE_CONFIRM_DEBTORS') : $t('DEBTIA_DEBTOR') }}
        </h5>
        <feather-icon
          icon="EditIcon"
          class="edit-icon"
        />
      </div>
      <div class="section-content">
        <div
          v-for="(debtor, index) in caseData.debtors"
          :key="debtor.id"
          class="debtor-summary"
          :class="{ 'mt-2': index > 0 }"
        >
          <h6 v-if="caseData.debtors.length > 1">
            {{ $t('DEBTIA_DEBTOR') }} {{ index + 1 }}
          </h6>
          <b-row>
            <b-col
              cols="12"
              md="6"
            >
              <p class="mb-1">
                <strong>{{ debtor.name || $t('DEBTIA_CREATE_CONFIRM_NO_NAME') }}</strong>
                <b-badge
                  v-if="debtor.isExisting"
                  variant="light-success"
                  class="ml-50"
                >
                  {{ $t('DEBTIA_COMMON_EXISTING') }}
                </b-badge>
              </p>
              <p class="mb-1 text-muted">
                {{ getDebtorTypeName(debtor.debtorType) }}
                <span v-if="debtor.cvr"> - CVR: {{ debtor.cvr }}</span>
                <span v-if="debtor.cpr"> - CPR: {{ maskCpr(debtor.cpr) }}</span>
              </p>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <p class="mb-1">
                {{ debtor.address.roadName }} {{ debtor.address.houseNumber }}
              </p>
              <p class="mb-0 text-muted">
                {{ debtor.address.zipCode }} {{ debtor.address.city }}
              </p>
            </b-col>
          </b-row>
          <p
            v-if="debtor.email || debtor.phone"
            class="mb-0 mt-1 text-muted"
          >
            <span v-if="debtor.email">{{ debtor.email }}</span>
            <span v-if="debtor.email && debtor.phone"> | </span>
            <span v-if="debtor.phone">{{ debtor.phone }}</span>
          </p>
        </div>
      </div>
    </b-card>

    <!-- Custom Variables (hvis der er nogen) - Step 3 i ny struktur -->
    <b-card
      v-if="hasCustomVariables"
      class="confirmation-section"
      @click="goToStep(3)"
    >
      <div class="section-header">
        <h5>
          <feather-icon
            icon="ListIcon"
            class="mr-50"
          />
          {{ $t('DEBTIA_CREATE_CONFIRM_GENERAL_INFO') }}
        </h5>
        <feather-icon
          icon="EditIcon"
          class="edit-icon"
        />
      </div>
      <div class="section-content">
        <b-row>
          <b-col
            v-for="variable in filledCustomVariables"
            :key="variable.customVarLabel"
            cols="12"
            md="6"
          >
            <p class="mb-1">
              <span class="text-muted">{{ formatVariableLabel(variable.customVarLabel) }}:</span>
              <strong class="ml-1">{{ variable.value }}</strong>
            </p>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Bekræftelse med checkbox -->
    <div
      class="confirmation-accept mt-2"
      :class="{ 'confirmation-accepted': caseData.confirmationAccepted }"
      @click="handleConfirmationChange(!caseData.confirmationAccepted)"
    >
      <div class="confirmation-check-icon">
        <feather-icon
          :icon="caseData.confirmationAccepted ? 'CheckSquareIcon' : 'SquareIcon'"
          :size="caseData.confirmationAccepted ? '22' : '22'"
        />
      </div>
      <span class="confirmation-label">
        {{ $t('DEBTIA_CREATE_CONFIRM_CHECKBOX_TEXT') }}
      </span>
    </div>
  </div>
</template>

<script>
/**
 * StepConfirmation Script
 *
 * Viser en opsummering af alle data inden endelig indsendelse.
 */
import {
  BCard,
  BRow,
  BCol,
  BBadge,
  BTableSimple,
  BThead,
  BTbody,
  BTfoot,
  BTr,
  BTh,
  BTd,
} from 'bootstrap-vue'
import { computed } from '@vue/composition-api'
import { useCaseCreation } from '../composables/useCaseCreation'

export default {
  name: 'StepConfirmation',

  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTableSimple,
    BThead,
    BTbody,
    BTfoot,
    BTr,
    BTh,
    BTd,
  },

  props: {
    caseData: {
      type: Object,
      required: true,
    },
    caseTypes: {
      type: Array,
      required: true,
    },
    debtorTypes: {
      type: Array,
      required: true,
    },
  },

  setup(props, { root }) {
    const { setConfirmationAccepted } = useCaseCreation()

    /**
     * Finder den valgte sagstype.
     */
    const selectedCaseType = computed(() => props.caseTypes.find(t => t.id === props.caseData.caseType.selectedType))

    /**
     * Beregner totalbeløb for alle grundlag dokumenter (fakturaer).
     */
    const totalAmount = computed(() => props.caseData.documents.grundlag.reduce((sum, doc) => sum + (parseFloat(doc.paymentAmount) || 0), 0))
    const totalPaid = computed(() => props.caseData.documents.grundlag.reduce((sum, doc) => sum + (parseFloat(doc.amountPaid) || 0), 0))

    /**
     * Tjekker om der er custom variables udfyldt.
     */
    const hasCustomVariables = computed(() => props.caseData.customVariables.values.length > 0)

    /**
     * Filtrerer kun udfyldte custom variables.
     */
    const filledCustomVariables = computed(() => props.caseData.customVariables.values.filter(v => v.value))

    /**
     * Formaterer en dato til dansk format.
     */
    function formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('da-DK')
    }

    /**
     * Formaterer et beløb med tusindtals-separator.
     */
    function formatAmount(amount) {
      if (!amount) return '0'
      const num = parseFloat(amount)
      return num.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    /**
     * Finder dansk navn for debtor type.
     */
    function getDebtorTypeName(type) {
      const found = props.debtorTypes.find(t => t.value === type)
      return found ? found.labelDa : type
    }

    /**
     * Maskerer CPR-nummer for privacy.
     */
    function maskCpr(cpr) {
      if (!cpr) return ''
      // Vis kun de første 6 cifre, masker resten
      return `${cpr.substring(0, 6)}-****`
    }

    /**
     * Formaterer en variabel-label til læsbar tekst.
     */
    function formatVariableLabel(label) {
      return label.replace(/_/g, ' ')
    }

    /**
     * Navigerer til et specifikt step.
     */
    function goToStep(stepNumber) {
      // Emit til parent for at skifte step
      root.$parent.goToStep(stepNumber)
    }

    /**
     * Håndterer ændring af bekræftelses-checkbox.
     */
    function handleConfirmationChange(value) {
      setConfirmationAccepted(value)
    }

    return {
      selectedCaseType,
      totalAmount,
      totalPaid,
      hasCustomVariables,
      filledCustomVariables,
      formatDate,
      formatAmount,
      getDebtorTypeName,
      maskCpr,
      formatVariableLabel,
      goToStep,
      handleConfirmationChange,
    }
  },
}
</script>

<style lang="scss" scoped>
.confirmation-section {
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 1rem;

  &:hover {
    border-color: #7367f0;
    box-shadow: 0 4px 15px rgba(115, 103, 240, 0.1);

    .edit-icon {
      opacity: 1;
    }
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e9ecef;

    h5 {
      margin-bottom: 0;
    }

    .edit-icon {
      opacity: 0;
      transition: opacity 0.2s ease;
      color: #7367f0;
    }
  }
}

.debtor-summary {
  padding-top: 0.5rem;
  border-top: 1px dashed #e9ecef;

  &:first-child {
    padding-top: 0;
    border-top: none;
  }
}

// Confirmation checkbox
.confirmation-accept {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: #f8fffe;
  border: 2px solid rgba(8, 140, 118, 0.25);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(8, 140, 118, 0.5);
    background: #f0faf8;
  }

  &.confirmation-accepted {
    border-color: #088c76;
    background: rgba(8, 140, 118, 0.06);
  }

  .confirmation-check-icon {
    flex-shrink: 0;
    color: #b0b8c0;
    margin-top: 0.1rem;
    transition: color 0.2s ease;
  }

  &.confirmation-accepted .confirmation-check-icon {
    color: #088c76;
  }

  .confirmation-label {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #5e5873;
  }
}

// ---- Dark Mode ----
.dark-layout {
  .confirmation-section {
    background: #091828;
    border-color: #163350;

    &:hover {
      border-color: #088c76;
      box-shadow: 0 4px 15px rgba(8, 140, 118, 0.15);
    }

    .section-header {
      border-color: #163350;

      .edit-icon {
        color: #088c76;
      }
    }

    .section-content {
      color: #c5cdd8;
    }
  }

  .debtor-summary {
    border-color: #163350;
  }

  .confirmation-accept {
    background: rgba(8, 140, 118, 0.06);
    border-color: rgba(8, 140, 118, 0.2);

    &:hover {
      border-color: rgba(8, 140, 118, 0.4);
      background: rgba(8, 140, 118, 0.1);
    }

    &.confirmation-accepted {
      border-color: #088c76;
      background: rgba(8, 140, 118, 0.12);
    }

    .confirmation-check-icon {
      color: #5a6570;
    }

    &.confirmation-accepted .confirmation-check-icon {
      color: #4fd1c5;
    }

    .confirmation-label {
      color: #c5cdd8;
    }
  }
}
</style>
